import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { generateIdempotencyKey } from '../../../lib/idempotency';
import { Gate } from '../../../components/permissions/Gate';
import { Button } from '../../../components/ui/button';
import { useRoleNameById } from '../../../hooks/useRoleNameById';
import { shortId } from '../../../lib/shortId';
import { displayNameOrShort } from '../../../lib/displayName';
import {
    RoleScopePicker,
    buildAssigns,
    hasValidAssigns,
    emptyRoleScope,
    type RoleScopeValue,
} from './RoleScopePicker';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Admin platform cuyas asignaciones se gestionan. */
    cognitoSub: string;
    adminLabel: string;
}

/**
 * Gestión COMPLETA de roles de un ADMIN PLATFORM: ver actuales / agregar / quitar. Es el equivalente
 * platform de `ManageUserRolesDialog` (que sirve a tenant/retailer): unifica en una sola acción lo que
 * antes estaban separados en "Asignar rol" (AssignRoleDialog) + "Ver asignaciones".
 *
 *  - "Roles actuales": lista cada `role-assignment` (roleId + scope: global/tenant) con un botón
 *    "Quitar" (revoke) gateado por `platform.role.update`. Revoca por `assignmentId`.
 *  - "Agregar rol": reusa `RoleScopePicker` (toggle GLOBAL / por-tenant) — el selector propio de la
 *    entidad platform (NO el RoleScopeFields rol→scope→scopeRef del tenant). El backend RECALCULA el
 *    effective scope desde el rol (220f7b4): global → scopeRef '*'; por-tenant → un assign por tenant.
 *
 * Overlay inline (no Radix Dialog) — patrón anti-freeze del repo, igual que AssignRoleDialog.
 */
export function ManageAdminRolesDialog({ open, onOpenChange, cognitoSub, adminLabel }: Props) {
    const qc = useQueryClient();
    const [value, setValue] = useState<RoleScopeValue>(emptyRoleScope);
    // assignmentId en curso de revocación (para deshabilitar solo ese botón).
    const [revokingId, setRevokingId] = useState<string | null>(null);

    const assignmentsKey = ['platform', 'admins', cognitoSub, 'role-assignments'];

    const { data: assignments, isLoading } = useQuery({
        queryKey: assignmentsKey,
        queryFn: () => platformApi.listAdminRoleAssignments(cognitoSub),
        enabled: open,
    });

    const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: assignmentsKey });

    const assignMut = useMutation({
        mutationFn: async () => {
            // Un assign por cada `{ roleId, scopeRef }` (global → uno con '*'; por-tenant → uno por tenant).
            for (const body of buildAssigns(value)) {
                await platformApi.assignAdminRole(cognitoSub, body, {
                    idempotencyKey: generateIdempotencyKey(),
                });
            }
        },
        onSuccess: async () => {
            toast.success('Rol asignado');
            setValue(emptyRoleScope);
            await invalidate();
        },
        onError: handleApiError,
    });

    const revokeMut = useMutation({
        mutationFn: (assignmentId: string) => platformApi.revokeAdminRole(cognitoSub, assignmentId),
        onMutate: (assignmentId: string) => {
            setRevokingId(assignmentId);
        },
        onSuccess: async () => {
            toast.success('Rol quitado');
            await invalidate();
        },
        onError: handleApiError,
        onSettled: () => {
            setRevokingId(null);
        },
    });

    // Resolvers id→nombre para mostrar nombre del rol y del tenant (no el UUID) en "Roles actuales".
    // Antes del early return (rules-of-hooks); el query se gatea por `open`.
    const roleNameById = useRoleNameById();
    const { data: tenantsData } = useQuery({
        queryKey: ['platform', 'tenants', 'admin-roles-map'],
        queryFn: () => platformApi.listTenants(),
        enabled: open,
    });

    if (!open) return null;

    const items = assignments?.items ?? [];
    const tenantNameById = new Map(
        (tenantsData?.items ?? []).map((t) => [t.tenantId, t.displayName]),
    );
    const canSubmit = hasValidAssigns(value);

    return (
        <div
            role="dialog"
            aria-label="Gestionar roles"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16"
        >
            <button
                type="button"
                aria-label="Cerrar"
                className="fixed inset-0 bg-black/50"
                onClick={() => {
                    onOpenChange(false);
                }}
            />
            <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                <div className="mb-1 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Gestionar roles</h2>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        ✕
                    </button>
                </div>
                <p className="mb-4 text-sm text-muted-foreground">{adminLabel}</p>

                {/* ── Roles actuales ── */}
                <div className="space-y-2">
                    <p className="text-sm font-medium">Roles actuales</p>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">Cargando…</p>
                    ) : items.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sin roles asignados</p>
                    ) : (
                        <ul className="space-y-1">
                            {items.map((a) => (
                                <li
                                    key={a.assignmentId}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm"
                                >
                                    <span className="flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                            {/* DEC-013: nombre del rol, nunca el roleId crudo. */}
                                            {displayNameOrShort(
                                                roleNameById.get(a.roleId),
                                                a.roleId,
                                            )}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {a.scope}
                                            {a.scopeRef !== '*'
                                                ? `: ${tenantNameById.get(a.scopeRef) ?? shortId(a.scopeRef)}`
                                                : ' (global)'}
                                        </span>
                                    </span>
                                    <Gate permission="platform.role.update" scope="PLATFORM">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            aria-label={`Quitar ${displayNameOrShort(roleNameById.get(a.roleId), a.roleId)}`}
                                            disabled={
                                                revokeMut.isPending && revokingId === a.assignmentId
                                            }
                                            onClick={() => {
                                                revokeMut.mutate(a.assignmentId);
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                            <span className="ml-1">Quitar</span>
                                        </Button>
                                    </Gate>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* ── Agregar rol ── */}
                <Gate permission="platform.role.update" scope="PLATFORM">
                    <div className="mt-6 border-t pt-4">
                        <p className="mb-3 text-sm font-medium">Agregar rol</p>
                        <RoleScopePicker
                            value={value}
                            onChange={setValue}
                            enabled={open}
                            idPrefix="mard"
                        />
                        <div className="mt-4 flex justify-end">
                            <Button
                                onClick={() => {
                                    assignMut.mutate();
                                }}
                                disabled={!canSubmit || assignMut.isPending}
                            >
                                Agregar rol
                            </Button>
                        </div>
                    </div>
                </Gate>

                <div className="mt-6 flex justify-end gap-2 border-t pt-4">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
}
